<template>
  <!-- One past game, told from the tracked team's side. The old version drew a
       neutral home/away pair and left you to work out which of the two teams
       the column was about; anchoring on "us" makes W/L and the score read in
       one direction. -->
  <li>
    <!-- Links through to the game's own page (the frozen line and how it
         settled) when we stored it; last season's rows stay plain. -->
    <component
      :is="to ? 'router-link' : 'div'"
      :to="to"
      class="flex items-center gap-2 py-2.5 sm:py-3 -mx-1 rounded-md px-1"
      :class="to && 'hover:bg-white/[0.03]'"
    >
    <span
      class="w-1 shrink-0 self-stretch rounded-full"
      :class="spineClass"
      aria-hidden="true"
    ></span>

    <TeamLogoChip
      :team="them.displayName"
      :abbr="them.abbr"
      :logo="them.logo"
      size="sm"
    />

    <div class="min-w-0 flex-1">
      <span class="block truncate text-xs">
        <span class="text-slate-500">{{ atHome ? "vs" : "@" }}</span>
        <span class="ml-1 font-bold text-slate-200">{{ them.abbr }}</span>
        <!-- There is room for the nickname once the columns are wide, and
             it saves reading the mark to work out who JAX or LAC is. -->
        <span class="ml-1.5 hidden text-slate-400 lg:inline">{{ them.name }}</span>
      </span>
      <span class="mt-0.5 block truncate text-[11px] text-slate-500">
        Wk {{ game.week }}
        <span class="text-slate-600">·</span>
        {{ date }}
      </span>
    </div>

    <span class="shrink-0 text-sm font-bold tabular-nums" :class="resultTextClass">
      {{ us.score }}<span class="mx-0.5 font-normal text-slate-600">–</span
      ><span class="font-semibold text-slate-400">{{ them.score }}</span>
    </span>
    </component>
  </li>
</template>

<script>
import TeamLogoChip from "./TeamLogoChip.vue";

export default {
  name: "PastGameRow",
  components: { TeamLogoChip },
  props: {
    game: { type: Object, required: true },
    // The team whose column this is, in /api/v1/games shape -- `name` is the
    // full display name there, which is what the schedule payload keys on.
    team: { type: Object, required: true },
  },
  computed: {
    to() {
      const id = this.game.gameId;
      return id ? { name: "Game", params: { id: String(id) } } : null;
    },
    isUsHome() {
      return this.matches(this.game.homeTeam);
    },
    atHome() {
      return this.isUsHome;
    },
    us() {
      return this.isUsHome ? this.game.homeTeam : this.game.awayTeam;
    },
    them() {
      return this.isUsHome ? this.game.awayTeam : this.game.homeTeam;
    },
    // Scores arrive as strings from ESPN's `displayValue`.
    result() {
      const ours = Number(this.us.score);
      const theirs = Number(this.them.score);
      if (Number.isNaN(ours) || Number.isNaN(theirs)) return "";
      if (ours > theirs) return "W";
      if (ours < theirs) return "L";
      return "T";
    },
    spineClass() {
      if (this.result === "W") return "bg-emerald-400";
      if (this.result === "L") return "bg-rose-500";
      return "bg-slate-600";
    },
    resultTextClass() {
      if (this.result === "W") return "text-white";
      if (this.result === "L") return "text-slate-500";
      return "text-slate-300";
    },
    date() {
      return new Date(this.game.date).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
      });
    },
  },
  methods: {
    // The schedule payload's `name` is the nickname and `displayName` the full
    // name; /api/v1/games sends only the full name. Match on either, plus the
    // abbreviation, so one shape mismatch does not silently pick the wrong
    // side of the game.
    matches(side) {
      return (
        side.displayName === this.team.name ||
        side.name === this.team.name ||
        side.abbr === this.team.abbr
      );
    },
  },
};
</script>
